import { onMounted, onUnmounted } from '@vue/composition-api';
import { EXTERNAL_DEPLOY, OPEN_DEPLOY } from '@/config/constant';
import { useRouter } from '@/router/useRouter';
import store from '@/store/index';
import { getUserInfo } from '@/utils/userInfo';
import { getServiceId } from '@/utils/serviceInfo';
import { composeEditorFaviconSet, getPlatformFavicon } from '@/utils/composeEditorFaviconSet';
const isOpenDeploy = OPEN_DEPLOY || EXTERNAL_DEPLOY;
const hasCustomModel = window.GLOBAL_INFO.configuredCustomModel;
const useProxy = isOpenDeploy || hasCustomModel;
/**
 * 构建 wuji-gogo 请求所需的鉴权 / 归属 header。
 *
 * 无 composition-API hook 依赖（用 store 单例而非 useStore），因此可以在
 * setup 之外、任意异步回调里调用——供 LogoSelector 等未挂载 GOGO_CONFIG 的
 * 生图入口显式取头后透传，补齐 wuji_user / wuji_project 归属。
 *
 * @param projectId 当前项目 id；建项目等尚无 id 的场景传空串即可（此时仅带 user）
 */
export function buildGogoHeaders(projectId) {
    if (useProxy) {
        return {
            'xy-project-id': projectId,
            'xy-env': 'dev',
        };
    }
    return {
        'x-wuji-user-id': getUserInfo()?.rtx ?? '',
        'x-wuji-project-id': projectId,
        'x-wuji-group-id': store?.state?.project?.projectInfo?.groupId ?? '',
        'x-wuji-service-id': getServiceId() ?? '',
    };
}
/**
 * 在加载 wuji-gogo editor CDN（会固化 apiBaseUrl/apiBasePath）之前，写入 proxy 所需的 GOGO_CONFIG。
 *
 * LogoSelector 等未走 useGogoConfig() 的入口必须先调用本方法，否则 genImage 会直连
 * wujipt.woa.com，在 CloudIDE / 外网等跨域场景下 Failed to fetch。
 *
 * - useProxy === false：不改 baseUrl/basePath，保持直连
 * - useProxy === true：仅 merge 写入 location.origin + /api/xy/gogo
 * - 不碰 headers：归属头由调用方 extraHeaders（如 LogoSelector）或 useGogoConfig 负责，
 *   避免无 projectId 的 ensure 把已有 xy-project-id 冲成空串
 * - 不删除已有 info / favicon / headers，可与编辑器挂载共存
 */
export function ensureGogoProxyConfig() {
    if (!useProxy)
        return;
    window.GOGO_CONFIG = {
        ...window.GOGO_CONFIG,
        baseUrl: location.origin,
        basePath: '/api/xy/gogo',
    };
}
function getGogoHeaders() {
    const router = useRouter();
    const projectId = String(router.route.value?.query?.projectid || '');
    return buildGogoHeaders(projectId);
}
function getGogoInfo() {
    const router = useRouter();
    return {
        username: getUserInfo()?.rtx ?? '',
        projectId: String(router.route.value?.query?.projectid || ''),
        serviceId: getServiceId() ?? '',
    };
}
// 注入 wuji-gogo 的配置
export function useGogoConfig() {
    const baseUrl = useProxy ? location.origin : undefined;
    const basePath = useProxy ? '/api/xy/gogo' : undefined;
    const headers = getGogoHeaders();
    const info = getGogoInfo();
    const config = {
        baseUrl,
        basePath,
        headers,
        info,
    };
    onMounted(() => {
        const platformFavicon = getPlatformFavicon();
        // 同步挂全量 config，含平台占位 favicon（若有）
        // 确保 gogo CDN 初始化 useFavicon() 时 GOGO_CONFIG.favicon 已有值，
        // 避免空窗期落回 gogo 内置 wuji-default.png
        window.GOGO_CONFIG = {
            ...config,
            ...(platformFavicon ? { favicon: platformFavicon } : {}),
        };
        // 异步合成应用标五态，成功后覆盖平台占位（复用 composeEditorFaviconSet 与长任务保持一致）
        const project = store?.state?.project?.baseProjectInfo;
        const logoUrl = project?.logo || project?.favicon || '';
        if (!logoUrl)
            return; // 无应用标，保持平台占位即可
        composeEditorFaviconSet(logoUrl)
            .then((set) => {
            if (!window.GOGO_CONFIG || !set)
                return;
            // 合成失败不注入，平台占位保持
            window.GOGO_CONFIG.favicon = set;
        })
            .catch((e) => {
            console.warn('[useGogoConfig] compose favicon failed', e);
        });
    });
    onUnmounted(() => {
        if (window.GOGO_CONFIG) {
            delete window.GOGO_CONFIG;
        }
    });
    return config;
}
